<template>
  <div
    ref="addToCartRef"
    :class="isOos && !pendingStock && !pendingStock ? 'add-to-cart__stock' : 'add-to-cart'"
  >
    <ProductPrice
      v-if="productPrices && !['GroupedProduct', 'GiftCardProduct'].includes(product?.__typename)"
      class="add-to-cart__price"
      :prices="productPrices"
      :show-currency="true"
      :show-vat-tag="false"
    />

    <ProductSavingsDiscount
      v-if="productPrices && !['GroupedProduct', 'GiftCardProduct'].includes(product?.__typename)"
      class="add-to-cart__savings"
      :prices="productPrices"
      :show-currency="true"
    />

    <slot name="below-promise" />

    <ProductSelector
      :key="product"
      :product="props.product"
      :has-error="hasError"
      :is-outlet-product="isOutletProduct"
      @selected="collect"
      @selected-options="onSelectedOptions"
      @submit="add"
    >
      <template #oos="{ oos, selectedOptions: oosSelectedOptions }">
        <ProductOutOfStockNotification
          v-if="oos.length && !isOutletProduct"
          :products="oos"
          :parent-product="product"
          :item-size="getItemSize(oosSelectedOptions)"
        />
      </template>
    </ProductSelector>

    <template v-if="!isOos">
      <ProductAvailabilityDeliveryPromise
        v-if="selectedSku?.length && selectedSku[0] && !['GroupedProduct', 'GiftCardProduct'].includes(product?.__typename)"
        :selected-sku="selectedSku"
      />

      <ProductAvailabilityOnlineOnly
        v-if="selectedSku?.length && selectedSku[0] && !['GroupedProduct', 'GiftCardProduct'].includes(product?.__typename)"
        :selected-sku="selectedSku"
      />

      <div
        v-if="hasError"
        ref="error"
        class="message message--error required"
      >
        <IconInfo class="message__icon icon icon--info" />
        <span>{{ $t(errorLabel) }}</span>
      </div>

      <div
        v-if="product?.__typename !== 'GroupedProduct'"
        class="add-to-cart__actions"
      >
        <button
          type="button"
          :disabled="pending || pendingStock"
          class="add-to-cart__cta button button--shop-flow button--full button--center"
          data-testid="add-to-cart"
          @click.prevent="add"
        >
          <IconShoppingcart
            class="add-to-cart__icon icon icon--shoppingcart"
          />
          <span>{{ $t('Add to cart') }}</span>
          <span v-if="groupedPrice > 0">-</span>
          <i18n-n
            v-if="groupedPrice > 0"
            tag="span"
            :value="groupedPrice"
            format="currency"
            scope="global"
          >
            <template #currency="slotProps">
              <span class="price__currency">{{ slotProps.currency }}</span>
            </template>
            <template #integer="slotProps">
              <span class="price__integer">{{ slotProps.integer }}</span>
            </template>
            <template #decimal="slotProps">
              <span class="price__decimal">{{ slotProps.decimal }}</span>
            </template>
            <template #fraction="slotProps">
              <span class="price__fraction">{{ slotProps.fraction }}</span>
            </template>
          </i18n-n>
          <template v-if="pending">
            <IconLoader class="icon icon--loader" />
          </template>
        </button>

        <ProductToggleWishlist
          class="add-to-cart__wishlist-toggle button button--link button--icon-only"
          :product="product"
        />
      </div>
    </template>
    <template v-else-if="!isOutletProduct">
      <div
        class="add-to-cart__oos-message message message--error"
        @click.prevent="triggerOosDialog"
      >
        <IconInfo class="message__icon icon icon--info" />
        <button
          type="button"
          class="add-to-cart__oos button button--link button--center"
          data-testid="add-to-cart"
          @click.prevent="triggerOosDialog"
        >
          <UiRichtext :html="$t('This product is currently out of stock. Please let me know when it’s back in stock.')" />
        </button>
      </div>
    </template>

    <ClientOnly>
      <Teleport
        defer
        to="#teleports"
      >
        <Transition name="slide">
          <div
            v-if="isPastAddToCart"
            class="pdp__sticky-add-to-cart"
          >
            <div class="pdp__sticky-add-to-cart--container">
              <div class="pdp__sticky-add-to-cart--button">
                <button
                  type="button"
                  :disabled="pending || pendingStock"
                  class="add-to-cart__cta button button--shop-flow button--full button--center"
                  data-testid="add-to-cart"
                  @click.prevent="add"
                >
                  <IconShoppingcart
                    class="add-to-cart__icon icon icon--shoppingcart"
                  />
                  <span>{{ $t('Add to cart') }}</span>
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </Teleport>
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import type {
  MagentoCartItemInput,
  MagentoConfigurableProductCartItemInput,
  MagentoConfigurableProduct,
  MagentoConfigurableProductOptionsValue,
  MagentoGroupedProduct,
  MagentoProduct,
} from '~layers/app/types/magento'

// const { storeConfig } = await useStore()

export interface Props {
  product: MagentoProduct | MagentoConfigurableProduct | MagentoGroupedProduct
  outletProduct?: number
}

const meta = inject('meta', ref())
const { addToCart, pending } = useCart()
const hasError = ref(false)
const error = ref(null)

const addToCartRef = ref(null)
const addToCartVisible = ref(true)

useIntersectionObserver(
  addToCartRef,
  ([entry]) => {
    addToCartVisible.value = entry?.isIntersecting || false
  },
  { rootMargin: '-10px 0px 0px 0px' },
)

const isPastAddToCart = computed(() => !addToCartVisible.value && (addToCartRef.value as HTMLElement | null)?.getBoundingClientRect().top < 0)

onClickOutside(error, () => {
  hasError.value = false
})

const selectedItems = ref<Record<string, MagentoCartItemInput | null>>({})
const selectedOptions = ref<Record<string, MagentoConfigurableProductOptionsValue>>({})

const props = defineProps<Props>()

const emit = defineEmits<{
  selected: Record<string, MagentoCartItemInput | MagentoConfigurableProductCartItemInput>
}>()

const {
  loadStockData,
  pendingStock,
} = usePDP(meta)

onMounted(() => {
  loadStockData()
})

const onSelectedOptions = (options: Record<string, MagentoConfigurableProductOptionsValue>) => {
  selectedOptions.value = options
}

const getItemSize = (options?: Record<string, MagentoConfigurableProductOptionsValue>) => {
  const opts = options || selectedOptions.value

  const sizeOption = opts?.del_size || opts?.size

  if (sizeOption) {
    return sizeOption.local_size || sizeOption.label || 'one-size'
  }

  return 'one-size'
}

const isOutletProduct = computed(() => {
  return !!(props.outletProduct && [2, 3].includes(props.outletProduct))
})

const variantPrices = computed<Record<string, number>>(() => {
  return props.product?.variants?.reduce((acc: Record<string, number>, variant: any) => {
    return {
      ...acc,
      [variant.product.sku]: {
        regularPrice: variant.product?.price_range?.minimum_price?.regular_price?.value,
        finalPrice: variant.product?.price_range?.minimum_price?.final_price?.value,
      },
    }
  }, {}) || {}
})

const selectedSku = computed(() => {
  if (props.product?.__typename == 'ConfigurableProduct') {
    const currentSku = Object.values(selectedItems.value).map((item: any) => item?.data?.sku)
    emit('selected', currentSku)
    return currentSku
  }

  emit('selected', props.product.sku)
  return [props.product.sku]
})

const productPrices = computed(() => {
  const sku = selectedItems.value?.[props.product.sku]?.data?.sku
  if (sku && variantPrices.value?.[sku]) {
    return variantPrices.value?.[sku]
  }
  return {
    fromPrice: props.product?.product?.type === 'configurable' ? props.product?.price_range?.minimum_price?.regular_price?.value : undefined,
    minPrice: props.product?.product?.type === 'configurable' ? props.product?.price_range?.minimum_price?.regular_price?.value : undefined,
    regularPrice: props.product?.price_range?.maximum_price?.regular_price?.value,
    finalPrice: props.product?.price_range?.minimum_price?.final_price?.value,
  }
})

const groupedPrice = computed(() => {
  return Object.values(selectedItems.value).reduce((acc: number, item: any) => {
    if (props.product?.__typename === 'GroupedProduct') {
      acc += variantPrices.value?.[item?.data?.sku] || 0
    }
    return acc
  }, 0)
})

const collect = (item: Record<string, MagentoCartItemInput | MagentoConfigurableProductCartItemInput | null>) => {
  selectedItems.value = {
    ...selectedItems.value,
    ...item,
  }
}

const isOos = computed(() => {
  if (props.product?.__typename === 'SimpleProduct') {
    return props.product.stock_status === 'OUT_OF_STOCK'
  }
  if (props.product?.__typename === 'ConfigurableProduct') {
    if (props.product.variants.every(item => item.product?.stock_status === 'OUT_OF_STOCK')) {
      return true
    }
    const selectedItemsArray = Object.values(selectedItems.value)
    if (selectedItemsArray.filter(item => item).length < selectedItemsArray.length) {
      return true
    }
  }
  return false
})

const { open: openOosDialog, settings: oosDialogSettings } = useUiDialog('product-oos-dialog')

const triggerOosDialog = () => {
  if (oosDialogSettings.value.isOpen) return
  openOosDialog()
}

const add = () => {
  console.log(selectedItems.value)
  const item = Object.values(selectedItems.value).filter(item => item)
  if (!item.length) {
    hasError.value = true
    addToCartRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  else {
    addToCart(item, false)
    hasError.value = false
  }
}

const errorLabel = computed(() => {
  if (props.product?.__typename === 'SimpleProduct') {
    return 'Please fill in all required fields'
  }
  if (props.product?.__typename === 'ConfigurableProduct') {
    return 'Please choose a size'
  }
  if (props.product?.__typename === 'GiftCardProduct') {
    return 'Please fill in all required fields above'
  }
  return 'Please fill in all required fields'
})
</script>

<style src="~layers/app/components/Product/AddToCart.css" />
